import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import {
  getFileBaseName,
  validateFileBaseName,
} from "../../utils/validateDisplayName";

type Props = {
  open: boolean;
  initialName: string;
  originalExtension: string;
  isSubmitting?: boolean;
  serverError?: string | null;
  onClose: () => void;
  onSubmit: (displayName: string) => void;
};

export function RenameModal({
  open,
  initialName,
  originalExtension,
  isSubmitting = false,
  serverError = null,
  onClose,
  onSubmit,
}: Props) {
  const [baseName, setBaseName] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);
  const extensionLabel = `.${originalExtension.toLowerCase()}`;

  useEffect(() => {
    if (!open) return;
    setBaseName(getFileBaseName(initialName, originalExtension));
    setClientError(null);
  }, [initialName, open, originalExtension]);

  const handleSubmit = () => {
    const validation = validateFileBaseName(baseName, originalExtension);
    if (!validation.ok) {
      setClientError(validation.message);
      return;
    }

    setClientError(null);
    onSubmit(validation.displayName);
  };

  const errorMessage = clientError ?? serverError;

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Renomear</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="Nome do arquivo"
          value={baseName}
          onChange={(event) => {
            setBaseName(event.target.value);
            if (clientError) setClientError(null);
          }}
          disabled={isSubmitting}
          error={!!errorMessage}
          helperText={errorMessage}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSubmit();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography
                  component="span"
                  fontSize={14}
                  fontWeight={500}
                  color="text.secondary"
                  sx={{ userSelect: "none", whiteSpace: "nowrap" }}
                >
                  {extensionLabel}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{ textTransform: "none" }}
        >
          {isSubmitting ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
